import type React from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from '@/components/ui/button';
import CodeInput from './CodeInput';
import { inlineLink, inlineLinkError, inlineLinkMuted } from './styles';

export type TokenMode = 'inbox' | 'redirecting' | 'expired';

type Props = {
  mode: TokenMode;
  email: string;
  token: string;
  onTokenChange: (value: string) => void;
  onSubmit: (code?: string) => void;
  onResend: () => void;
  onStartOver: () => void;
  onUseDifferentEmail: () => void;
  resendPending: boolean;
  codeResent: boolean;
  isPending: boolean;
  error: string | null;
  invalidCode: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
};

const TokenStep = ({
  mode,
  email,
  token,
  onTokenChange,
  onSubmit,
  onResend,
  onStartOver,
  onUseDifferentEmail,
  resendPending,
  codeResent,
  isPending,
  error,
  invalidCode,
  inputRef,
}: Props) => {
  const hasError = !!error || invalidCode;
  if (mode === 'redirecting') {
    return (
      <h2 className="tw:text-xl tw:font-medium tw:text-gray-700 tw:mb-6 tw:text-center">
        <FormattedMessage
          id="sign-in-page-form-redirecting"
          defaultMessage="Redirecting..."
        />
      </h2>
    );
  }

  if (mode === 'expired') {
    return (
      <>
        <h2 className="tw:text-xl tw:font-medium tw:text-gray-700 tw:mb-4 tw:text-center">
          <FormattedMessage
            id="sign-in-page-form-authentication"
            defaultMessage="Authentication"
          />
        </h2>
        <p className="tw:text-sm tw:text-gray-600 tw:mb-6 tw:text-center">
          <FormattedMessage
            id="sign-in-page-form-no-longer-valid"
            defaultMessage="The sign in link is no longer valid."
          />{' '}
          <button type="button" onClick={onStartOver} className={inlineLink}>
            <FormattedMessage
              id="sign-in-page-form-start-over"
              defaultMessage="Please start over"
            />
          </button>
        </p>
      </>
    );
  }

  return (
    <>
      <h2 className="tw:text-xl tw:font-medium tw:text-gray-700 tw:mb-4 tw:text-center">
        <FormattedMessage
          id="sign-in-page-form-title-check-your-inbox"
          defaultMessage="Check your inbox"
        />
      </h2>
      <p className="tw:text-sm tw:text-gray-600 tw:pb-0 tw:mb-1 tw:text-center">
        <FormattedMessage
          id="sign-in-code-sent-message"
          defaultMessage="Enter the code sent to {email}."
          values={{
            email: <strong className="tw:text-gray-900">{email}</strong>,
          }}
        />
      </p>
      <p className="tw:text-sm tw:text-gray-600 tw:mb-5 tw:pb-0 tw:text-center">
        <FormattedMessage
          id="sign-in-code-valid-duration"
          defaultMessage="The code is valid for 15 minutes."
        />
      </p>

      <div className="tw:mb-6">
        <CodeInput
          inputRef={inputRef}
          value={token}
          onChange={onTokenChange}
          onComplete={onSubmit}
          hasError={hasError}
          disabled={isPending}
        />
        {invalidCode ? (
          <p className="tw:mt-3 tw:pb-0 tw:text-sm tw:text-destructive tw:text-center">
            <FormattedMessage
              id="sign-in-code-invalid"
              defaultMessage="This sign in code is no longer valid. Please <link>request a new code</link>."
              values={{
                link: (chunks) => (
                  <button
                    type="button"
                    onClick={onResend}
                    disabled={resendPending}
                    className={inlineLinkError}
                  >
                    {chunks}
                  </button>
                ),
              }}
            />
          </p>
        ) : (
          error && (
            <p className="tw:mt-3 tw:pb-0 tw:text-sm tw:text-destructive tw:text-center">
              {error}
            </p>
          )
        )}
        {codeResent && !hasError && (
          <p className="tw:mt-3 tw:pb-0 tw:text-sm tw:text-gray-600 tw:text-center">
            <FormattedMessage
              id="sign-in-code-resent"
              defaultMessage="A new code has been sent."
            />
          </p>
        )}
      </div>

      <Button
        type="button"
        size="lg"
        onClick={() => onSubmit()}
        disabled={isPending}
        className="tw:w-48 tw:rounded-full"
      >
        <FormattedMessage id="sign-in-form-signin" defaultMessage="Sign in" />
      </Button>

      <p className="tw:mt-6 tw:pb-0 tw:mb-2 tw:text-sm tw:text-center">
        <button
          type="button"
          onClick={onResend}
          disabled={resendPending}
          className={inlineLink}
        >
          <FormattedMessage
            id="sign-in-resend-button"
            defaultMessage="Resend code"
          />
        </button>
      </p>
      <p className="tw:mt-1 tw:text-xs tw:text-gray-400 tw:text-center">
        <FormattedMessage
          id="sign-in-spam-hint"
          defaultMessage="Not seeing it? Check your spam or junk folder."
        />
      </p>
      <p className="tw:mt-0 tw:text-sm tw:text-center">
        <button
          type="button"
          onClick={onUseDifferentEmail}
          className={inlineLinkMuted}
        >
          <FormattedMessage
            id="sign-in-use-different-email"
            defaultMessage="Use a different email"
          />
        </button>
      </p>
    </>
  );
};

export default TokenStep;
